// Shared machine control panel (Start / Pause / Unpause / Stop / Reset) with a
// live state badge. Used by Dashboard, Setup, Maintenance, and the global
// Topbar via three layout variants.
import React, { useState } from "react";
import { Box, Typography, Button, useTheme } from "@mui/material";
import { tokens } from "../theme";
import useMachineState from "../hooks/useMachineState";
import { useAppContext } from "../context/AppContext";
import Popup from "./Popup";
import api from "../services/api";

/**
 * Machine control panel with PackML-aware button gating.
 * @param {Object} props
 * @param {'vertical'|'horizontal'|'topbar'} props.layout - 'vertical' (Dashboard
 *   fallback panel), 'horizontal' (Setup / Maintenance footer), 'topbar'
 *   (compact single-row variant; suppresses the OFFLINE chip since the topbar
 *   bell already shows connection state).
 * @param {number} props.activeRecipesCount - Number of active recipes (for disabling Start).
 * @param {Function} [props.onStop] - Optional callback after stop (Setup uses it to move recipes back).
 * @param {Object} [props.styles] - Style overrides (titleVariant, buttonHeight,
 *   buttonFontSize, buttonGap, stateBadge, recipesTextVariant).
 * @param {boolean} [props.showTitle=true] - Renders the "Machine Controls" heading.
 * @param {'default'|'reversed'} [props.contentOrder='default'] - Buttons-first when reversed.
 * @param {Object} [props.machineStateOverride] - External machine-state object; when
 *   set, the internal useMachineState hook is disabled.
 */
const MachineControls = ({ 
  layout = 'vertical', 
  activeRecipesCount = 0, 
  onStop,
  styles = {},
  showTitle = true,
  contentOrder = 'default',
  machineStateOverride,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  
  const ownState = useMachineState({ disabled: !!machineStateOverride });
  const { state: machineState, activeRecipes, isConnected, staleGates } = machineStateOverride || ownState;
  const { assignedRecipes, setAssignedRecipes } = useAppContext();
  
  // Use prop or hook for recipe count
  const recipeCount = activeRecipesCount || activeRecipes?.length || 0;
  
  // Default styles with overrides
  const {
    titleVariant = 'h4',
    buttonHeight = '40px',
    buttonFontSize = '1.0rem',
    buttonGap = layout === 'vertical' ? 1.5 : 3,
    stateBadge = {},
    recipesTextVariant = 'body2',
  } = styles;
  
  // State badge defaults
  const {
    px: badgePx = 2,
    py: badgePy = 0.5,
    borderRadius: badgeBorderRadius = 0.5,
    fontSize: badgeFontSize = '0.875rem',
    // Pill height is now decoupled from font size for the topbar
    // variant — set this to size the pill, set fontSize to size
    // the text. `null` = let the box size itself from font + py
    // (legacy behaviour for the horizontal/vertical variants).
    height: badgeHeight = null,
  } = stateBadge;

  // CRITICAL: while paused the PLC is in PackML Suspended; the only legal exit
  // is UnSuspend (the `resume` verb). See docs/design-decisions.md#ui-packml-pause-unsuspend
  const handleStart = async () => {
    if (recipeCount === 0) {
      console.error('Cannot start: No active recipes');
      return;
    }
    if (machineState === "running") {
      console.error('Machine is already running');
      return;
    }
    if (machineState === "paused") {
      console.error('Machine is paused — press Unpause instead of Start');
      return;
    }
    if (machineState === "stopped") {
      console.error('Machine is stopped — press Reset before Start');
      return;
    }

    try {
      console.log('[MachineControls] Starting machine...');
      const response = await api.post('/machine/control', { action: 'start' });
      console.log('[MachineControls] Machine started:', response.data);
    } catch (error) {
      console.error('[MachineControls] Failed to start machine:', error);
    }
  };

  const handlePause = async () => {
    if (machineState !== "running") {
      console.error('Cannot pause: Machine is not running');
      return;
    }
    
    try {
      console.log('[MachineControls] Pausing machine...');
      const response = await api.post('/machine/control', { action: 'pause' });
      console.log('[MachineControls] Machine paused:', response.data);
    } catch (error) {
      console.error('[MachineControls] Failed to pause machine:', error);
    }
  };

  // Unpause = PackML UnSuspend, routed as the `resume` verb (Start is illegal
  // from Suspended).
  const handleResume = async () => {
    if (machineState !== "paused") {
      console.error(`Cannot unpause: Machine is not paused (current: ${machineState})`);
      return;
    }
    try {
      console.log('[MachineControls] Unpausing machine (PackML UnSuspend)...');
      const response = await api.post('/machine/control', { action: 'resume' });
      console.log('[MachineControls] Machine unpaused:', response.data);
    } catch (error) {
      console.error('[MachineControls] Failed to unpause machine:', error);
    }
  };

  const handleStop = async () => {
    if (machineState === "idle" || machineState === "stopped") {
      console.error(`Cannot stop from state: ${machineState}`);
      return;
    }
    if (machineState === "paused") {
      // The PLC rejects PackML_Stop from Suspended; operator must Unpause first.
      console.error('Cannot stop from paused — press Unpause first, then Stop');
      return;
    }
    
    try {
      console.log('[MachineControls] Stopping machine...');
      
      const response = await api.post('/machine/control', { action: 'stop' });
      console.log('[MachineControls] Machine stopped. Press Reset to re-enable Start.');

      if (onStop) {
        onStop();
      }
    } catch (error) {
      console.error('[MachineControls] Failed to stop machine:', error);
    }
  };

  // Reset confirms first: the machine returns to idle keeping the displayed
  // gate contents, and the next Start primes the algorithm from that state.
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const handleResetClick = () => {
    if (machineState !== "stopped") {
      console.error(`Reset is only valid from 'stopped' (current: ${machineState})`);
      return;
    }
    setResetDialogOpen(true);
  };

  const handleResetConfirm = async () => {
    setResetDialogOpen(false);
    try {
      console.log('[MachineControls] Resetting machine...');
      const response = await api.post('/machine/control', { action: 'reset' });
      console.log('[MachineControls] Machine reset → idle:', response.data);
    } catch (error) {
      console.error('[MachineControls] Failed to reset machine:', error);
    }
  };

  // Get state display info
  const getStateInfo = () => {
    switch (machineState) {
      case 'running':
        return { label: 'RUNNING', color: colors.tealAccent[500], text: 'Machine Running' };
      case 'paused':
        return { label: 'PAUSED', color: colors.orangeAccent[500], text: 'Machine Halted' };
      case 'transitioning':
        return { label: 'TRANSITIONING', color: colors.purpleAccent[500], text: 'Transitioning' };
      case 'estop':
        return { label: 'E-STOP', color: colors.redAccent[600], text: 'Emergency Stop active' };
      case 'stopped':
        return { label: 'STOPPED', color: colors.redAccent[600], text: 'Machine Stopped — press Reset' };
      default:
        return { label: 'IDLE', color: colors.redAccent[500], text: 'Machine Idle' };
    }
  };

  const stateInfo = getStateInfo();
  const inEstop = machineState === 'estop';

  const isVertical = layout === 'vertical';
  const isTopbar = layout === 'topbar';
  
  // Button styles with customizable dimensions
  const buttonBaseStyle = {
    color: '#fff',
    height: buttonHeight,
    fontSize: buttonFontSize,
    borderRadius: 1,
    fontWeight: 'bold',
  };

  // CRITICAL: enable/disable matrix mirrors the server-side /control route —
  // while paused only Unpause is legal (PackML Suspended).
  // See docs/design-decisions.md#ui-packml-pause-unsuspend
  const isPausedState = machineState === 'paused';
  // Leftover gate contents never block Start; staleGates only makes the Reset
  // confirmation dialog more specific.
  const staleGateList = staleGates || [];
  const startDisabled =
    inEstop ||
    recipeCount === 0 ||
    machineState === 'running' ||
    machineState === 'paused' ||
    machineState === 'transitioning' ||
    machineState === 'stopped';
  const pauseDisabled = inEstop || machineState !== 'running';
  const unpauseDisabled = inEstop || !isPausedState;
  const stopDisabled =
    inEstop ||
    machineState === 'idle' ||
    machineState === 'paused' ||
    machineState === 'stopped' ||
    machineState === 'transitioning';
  const resetDisabled = inEstop || machineState !== 'stopped';

  const disabledSx = {
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
    color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
  };

  const buttonsBlock = (
    <Box 
      display="flex" 
      flexDirection={isVertical ? 'column' : 'row'}
      gap={buttonGap}
      sx={{ flex: isVertical && contentOrder === 'default' ? 1 : 'none' }}
    >
      <Button
        variant="contained"
        onClick={handleStart}
        disabled={startDisabled}
        sx={{
          ...buttonBaseStyle,
          flex: isVertical ? 'none' : 1,
          width: isVertical ? '100%' : 'auto',
          backgroundColor: colors.tealAccent[500],
          '&:hover': { backgroundColor: colors.tealAccent[600] },
          '&.Mui-disabled': disabledSx,
        }}
      >
        START
      </Button>
      {isPausedState ? (
        // Unpause occupies Pause's slot but is visually inverted (orange
        // outline) so it reads as "release", not "halt".
        <Button
          variant="outlined"
          onClick={handleResume}
          disabled={unpauseDisabled}
          title="PackML UnSuspend — resume from Suspended back to Executing"
          sx={{
            ...buttonBaseStyle,
            color: colors.orangeAccent[500],
            backgroundColor: 'transparent',
            border: `2px solid ${colors.orangeAccent[500]}`,
            flex: isVertical ? 'none' : 1,
            width: isVertical ? '100%' : 'auto',
            '&:hover': {
              backgroundColor: isDark
                ? 'rgba(255, 152, 0, 0.08)'
                : 'rgba(255, 152, 0, 0.06)',
              border: `2px solid ${colors.orangeAccent[600]}`,
              color: colors.orangeAccent[600],
            },
            '&.Mui-disabled': {
              ...disabledSx,
              border: `2px solid ${isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.16)'}`,
              backgroundColor: 'transparent',
            },
          }}
        >
          UNPAUSE
        </Button>
      ) : (
        <Button
          variant="contained"
          onClick={handlePause}
          disabled={pauseDisabled}
          sx={{
            ...buttonBaseStyle,
            flex: isVertical ? 'none' : 1,
            width: isVertical ? '100%' : 'auto',
            backgroundColor: colors.orangeAccent[500],
            '&:hover': { backgroundColor: colors.orangeAccent[600] },
            '&.Mui-disabled': disabledSx,
          }}
        >
          PAUSE
        </Button>
      )}
      <Button
        variant="contained"
        onClick={handleStop}
        disabled={stopDisabled}
        sx={{
          ...buttonBaseStyle,
          flex: isVertical ? 'none' : 1,
          width: isVertical ? '100%' : 'auto',
          backgroundColor: colors.redAccent[500],
          '&:hover': { backgroundColor: colors.redAccent[600] },
          '&.Mui-disabled': disabledSx,
        }}
      >
        STOP
      </Button>
      <Button
        variant="contained"
        onClick={handleResetClick}
        disabled={resetDisabled}
        title={
          machineState === 'stopped'
            ? 'PackML Reset — clears the Stopped state and re-enables Start'
            : 'Reset is only available after Stop'
        }
        sx={{
          ...buttonBaseStyle,
          flex: isVertical ? 'none' : 1,
          width: isVertical ? '100%' : 'auto',
          backgroundColor: colors.purpleAccent[500],
          '&:hover': { backgroundColor: colors.purpleAccent[600] },
          '&.Mui-disabled': disabledSx,
        }}
      >
        RESET
      </Button>
    </Box>
  );

  // Topbar variant: state badge left of the buttons with the "N recipes
  // active" caption tucked underneath; horizontal/vertical keep row-wrap.
  const infoBlock = isTopbar ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // space-between pins the badge to the top and the caption to the
        // bottom of the buttons-row height (parent stretches this column).
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        py: 0,
      }}
    >
      <Box
        sx={{
          backgroundColor: stateInfo.color,
          color: '#fff',
          // badgeHeight sizes the pill, badgeFontSize sizes only the text;
          // flex-centering decouples the two. py is omitted — height owns
          // vertical sizing.
          display: 'flex',
          alignItems: 'center',
          height: badgeHeight,
          px: badgePx,
          borderRadius: badgeBorderRadius,
          fontWeight: 'bold',
          fontSize: badgeFontSize,
          lineHeight: 1.0,
          whiteSpace: 'nowrap',
        }}
      >
        {stateInfo.label}
      </Box>
      {/* "N recipes active" caption. No OFFLINE chip here — the topbar bell
          badge already exposes connection state. */}
      <Typography
        variant="caption"
        sx={{
          color: isDark ? colors.grey[400] : colors.grey[700],
          fontSize: '0.6rem',
          lineHeight: 1,
          letterSpacing: '0.02em',
          whiteSpace: 'nowrap',
        }}
      >
        {recipeCount} {recipeCount === 1 ? 'recipe' : 'recipes'} active
      </Typography>
    </Box>
  ) : (
    <Box sx={{
      mb: contentOrder === 'reversed' ? 0 : 2,
      mt: contentOrder === 'reversed' ? 1 : 0,
    }}>
      <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap"
        sx={contentOrder === 'reversed' ? { flexDirection: 'column-reverse', alignItems: 'flex-start' } : {}}
      >
        <Box
          sx={{
            backgroundColor: stateInfo.color,
            color: '#fff',
            px: badgePx,
            py: badgePy,
            borderRadius: badgeBorderRadius,
            fontWeight: 'bold',
            fontSize: badgeFontSize,
          }}
        >
          {stateInfo.label}
        </Box>
        <Typography
          variant={recipesTextVariant}
          sx={{ color: isDark ? '#fff' : '#000' }}
        >
          {recipeCount} {recipeCount === 1 ? 'recipe' : 'recipes'} active
        </Typography>
        {!isConnected && (
          <Box
            sx={{
              backgroundColor: colors.redAccent[600],
              color: '#fff',
              px: badgePx * 0.75,
              py: badgePy,
              borderRadius: badgeBorderRadius,
              fontWeight: 'bold',
              fontSize: parseFloat(badgeFontSize) * 0.85 + 'rem',
            }}
          >
            OFFLINE
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: isTopbar ? 'row' : 'column',
      // Topbar: stretch lets the badge column inherit the buttons-row height.
      alignItems: 'stretch',
      gap: isTopbar ? 1.5 : 0,
      height: '100%',
    }}>
      {showTitle && (
        <Typography variant={titleVariant} fontWeight="bold" sx={{ mb: 2, color: colors.tealAccent[500] }}>
          Machine Controls
        </Typography>
      )}
      {contentOrder === 'reversed' && !isTopbar ? (
        <>
          {buttonsBlock}
          {infoBlock}
        </>
      ) : (
        <>
          {infoBlock}
          {buttonsBlock}
        </>
      )}

      {/* Reset confirmation — reminds the operator that gate contents carry
          over; shared Popup keeps styling consistent with other confirms. */}
      <Popup
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        onConfirm={handleResetConfirm}
        title="Reset machine to idle?"
        confirmLabel="Confirm Reset"
        confirmColor={colors.purpleAccent[500]}
        message={
          <Box sx={{ whiteSpace: 'normal', maxWidth: 440 }}>
            <Box sx={{ mb: staleGateList.length > 0 ? 1.5 : 0 }}>
              After Reset, the machine <b>continues from the gate contents
              currently shown in the Gates section</b> — nothing is cleared
              automatically. To restart a gate empty, clear it before pressing
              Start.
            </Box>
            {staleGateList.length > 0 && (
              <Box>
                Gate{staleGateList.length > 1 ? 's' : ''} <b>{staleGateList.join(', ')}</b> currently
                {staleGateList.length > 1 ? ' hold' : ' holds'} leftover pieces from an
                incomplete batch.
              </Box>
            )}
          </Box>
        }
      />
    </Box>
  );
};

export default MachineControls;

